import { Button } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { useMemo } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { FilterConditionDTO } from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  useExpenseRecordsQuery,
  useExpensesSummaryQuery,
} from "../api/queries";
import { ExpenseRecordsFilter } from "../ui/ExpenseRecordsFilter";
import { ExpenseRecordsTable } from "../ui/ExpenseRecordsTable";
import {
  ExpenseSummaryCards,
  type ExpenseSummaryStatusFilter,
} from "../ui/ExpenseSummaryCards";

export function ExpenseRecordsPage() {
  const nav = useNavigate({ from: "/app/expenses" });
  const canCreate = useActionPermission(["expenses:create"]);
  const search = useSearch({ from: "/app/expenses" }) as Record<
    string,
    unknown
  >;

  const sortArray = useMemo((): Array<{
    field: string;
    dir: "asc" | "desc";
  }> => {
    const s = search.sort;
    if (Array.isArray(s) && s[0]) {
      const first = s[0] as { field?: string; dir?: string };
      return [
        {
          field: first.field ?? "expenseDate",
          dir: first.dir === "asc" ? "asc" : "desc",
        },
      ];
    }
    if (typeof s === "string" && s) {
      const field = s.startsWith("-") ? s.slice(1) : s;
      const dir: "asc" | "desc" = s.startsWith("-") ? "desc" : "asc";
      return [{ field, dir }];
    }
    return [{ field: "expenseDate", dir: "desc" }];
  }, [search.sort]);

  const query = useMemo(
    () => ({
      limit: Number(search.limit ?? 20),
      offset: Number(search.offset ?? 0),
      sort: sortArray,
      filters: search.filters as
        | Array<{ field: string; op: string; value: unknown }>
        | undefined,
    }),
    [search.limit, search.offset, sortArray, search.filters],
  );

  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const summaryQuery = useMemo(() => {
    const withoutStatus = removeConditions(filters, "status");
    return {
      filters: withoutStatus?.length ? withoutStatus : undefined,
    };
  }, [filters]);

  const { data, isLoading } = useExpenseRecordsQuery(query);
  const { data: summary, isLoading: summaryLoading } =
    useExpensesSummaryQuery(summaryQuery);

  const statusFilter = useMemo((): ExpenseSummaryStatusFilter => {
    const condition = findCondition(filters, "status");
    if (condition?.op === "eq" && typeof condition.value === "string") {
      if (
        condition.value === "submitted" ||
        condition.value === "approved" ||
        condition.value === "paid"
      ) {
        return condition.value;
      }
    }
    return "all";
  }, [filters]);

  const updateSearch = (updates: Record<string, unknown>) => {
    nav({ search: { ...search, ...updates } });
  };

  const handleSummaryStatusFilter = (status: ExpenseSummaryStatusFilter) => {
    const nextStatus =
      statusFilter === status && status !== "all" ? "all" : status;
    let nextFilters = filters;

    if (nextStatus === "all") {
      nextFilters = removeConditions(filters, "status") ?? [];
    } else {
      nextFilters =
        upsertCondition(filters, {
          field: "status",
          op: "eq",
          value: nextStatus,
        }) ?? [];
    }

    updateSearch({
      offset: 0,
      filters: nextFilters.length ? nextFilters : undefined,
    });
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/expenses/overview" },
            { label: "ບັນທຶກຄ່າໃຊ້ຈ່າຍ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl">ບັນທຶກຄ່າໃຊ້ຈ່າຍ</h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ລາຍການຄ່າໃຊ້ຈ່າຍຂອງອົງກອນ
            </p>
          </div>
          {canCreate && (
            <Button onClick={() => nav({ to: "/app/expenses/new" })}>
              <PlusIcon className="mr-2 size-4" />
              ບັນທຶກໃໝ່
            </Button>
          )}
        </div>

        <ExpenseSummaryCards
          summary={summary}
          isLoading={summaryLoading}
          statusFilter={statusFilter}
          onStatusFilterChange={handleSummaryStatusFilter}
        />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <ExpenseRecordsFilter />
          <ExpenseRecordsTable
            data={data?.data ?? []}
            isLoading={isLoading}
            offset={Number(search.offset ?? 0)}
            limit={Number(search.limit ?? 20)}
            totalCount={data?.meta?.total ?? 0}
            sortBy={query.sort?.[0]?.field ?? "expenseDate"}
            sortOrder={(query.sort?.[0]?.dir ?? "desc") as "asc" | "desc"}
            onPaginationChange={(offset, limit) =>
              updateSearch({ offset, limit })
            }
            onSortingChange={(id, desc) =>
              updateSearch({
                sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                offset: 0,
              })
            }
            onView={(id) => {
              nav({ to: "/app/expenses/$id", params: { id } });
            }}
          />
        </div>
      </Main>
    </>
  );
}
